import { useEffect, useState } from 'react';
import { http, formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Sparkles, Mail, FileText, ScrollText, Map, Copy, Check } from 'lucide-react';

const TABS = [
  { id: 'email', label: 'Email Draft', icon: Mail, description: "Draft a warm, professional email in your voice." },
  { id: 'listing', label: 'Listing Copy', icon: FileText, description: 'Craft luxury MLS copy from property details.' },
  { id: 'contract', label: 'Contract Summary', icon: ScrollText, description: 'Reduce any contract to a 1-page brief.' },
  { id: 'market', label: 'Market Intel', icon: Map, description: 'Hyper-local brief for any neighborhood.' },
];

export default function AIEmployee() {
  const [tab, setTab] = useState('email');
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try { const { data } = await http.get('/ai/history'); setHistory(data); }
    catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { loadHistory(); }, []);

  return (
    <div className="space-y-8">
      <header className="hq-fade-in">
        <p className="hq-eyebrow">The AI Employee</p>
        <h1 className="hq-h1 hq-page-title mt-3" data-testid="ai-employee-title">Your Deputy, Always Awake.</h1>
        <div className="hq-divider-gold w-fit"></div>
        <p className="mt-4 max-w-3xl" style={{ color: 'var(--ink-soft)', lineHeight: 1.7 }}>
          Powered by Claude Sonnet 5. HomiHQ observes, prepares, and drafts — you approve.
          Choose a task to begin.
        </p>
      </header>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap" data-testid="ai-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className="px-5 py-3 text-xs uppercase tracking-widest transition"
            style={{
              background: tab === t.id ? 'var(--sage)' : 'transparent',
              color: tab === t.id ? '#fff' : 'var(--sage)',
              border: '1px solid ' + (tab === t.id ? 'var(--sage)' : 'var(--line)'),
              borderRadius: 2,
            }}
          >
            <t.icon size={13} className="inline mr-2" strokeWidth={1.5} />
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {tab === 'email' && <EmailPanel onDone={loadHistory} />}
          {tab === 'listing' && <ListingPanel onDone={loadHistory} />}
          {tab === 'contract' && <ContractPanel onDone={loadHistory} />}
          {tab === 'market' && <MarketPanel onDone={loadHistory} />}
        </div>

        <aside className="hq-card p-6">
          <p className="hq-eyebrow">Recent Drafts</p>
          <h3 className="hq-section-title text-xl mt-1">Audit Trail</h3>
          <div className="hq-divider-gold mb-5"></div>
          <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2" data-testid="ai-history">
            {history.length === 0 && (
              <div className="text-sm py-8 text-center" style={{ color: 'var(--muted)' }}>
                Nothing here yet. Every AI draft will be logged.
              </div>
            )}
            {history.map(h => (
              <div key={h.id} className="pb-4" style={{ borderBottom: '1px solid var(--line-soft)' }}>
                <div className="flex justify-between items-center mb-1">
                  <span className="hq-eyebrow" style={{ color: 'var(--gold-soft)' }}>{h.type}</span>
                  <span className="text-xs" style={{ color: 'var(--muted)' }}>
                    {new Date(h.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="text-sm line-clamp-3" style={{ color: 'var(--ink-soft)' }}>
                  {(h.output || '').slice(0, 140)}…
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ─── Panels ────────────────────── */
function OutputCard({ loading, output, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(output); setCopied(true); setTimeout(() => setCopied(false), 1200); };
  if (loading) return <div className="mt-6 hq-shimmer h-60 rounded-sm"></div>;
  if (!output) return null;
  return (
    <div className="mt-6 p-6 hq-fade-in" style={{ background: 'var(--parchment)', border: '1px solid var(--gold)', borderRadius: 2 }}>
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={14} style={{ color: 'var(--gold)' }} />
          <span className="hq-eyebrow" style={{ color: 'var(--gold-soft)' }}>Draft</span>
        </div>
        <button onClick={copy} className="hq-btn-ghost">
          {copied ? <><Check size={12} className="inline mr-1"/> Copied</> : <><Copy size={12} className="inline mr-1"/> Copy</>}
        </button>
      </div>
      <pre className="whitespace-pre-wrap font-serif text-base" style={{ color: 'var(--ink)', lineHeight: 1.75 }} data-testid={testid}>
        {output}
      </pre>
    </div>
  );
}

function EmailPanel({ onDone }) {
  const [f, setF] = useState({ recipient_name: '', purpose: '', context: '', tone: 'warm' });
  const [output, setOutput] = useState(''); const [loading, setLoading] = useState(false);
  const run = async e => {
    e.preventDefault(); setLoading(true); setOutput('');
    try { const { data } = await http.post('/ai/email', f); setOutput(data.output); onDone(); }
    catch (er) { toast.error(formatError(er)); }
    setLoading(false);
  };
  return (
    <form onSubmit={run} className="hq-card p-8 space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div><label className="hq-label">Recipient Name</label><input className="hq-input" value={f.recipient_name} onChange={e => setF({...f, recipient_name: e.target.value})} placeholder="Ainsley Grant" /></div>
        <div><label className="hq-label">Tone</label>
          <select className="hq-input" value={f.tone} onChange={e => setF({...f, tone: e.target.value})}>
            <option value="warm">Warm</option>
            <option value="professional">Professional</option>
            <option value="urgent">Urgent</option>
            <option value="celebratory">Celebratory</option>
          </select>
        </div>
      </div>
      <div><label className="hq-label">Purpose</label><input className="hq-input" required data-testid="email-purpose" value={f.purpose} onChange={e => setF({...f, purpose: e.target.value})} placeholder="Follow up on Beacon Hill showing" /></div>
      <div><label className="hq-label">Context</label><textarea className="hq-input" rows={5} required value={f.context} onChange={e => setF({...f, context: e.target.value})} placeholder="They toured Sat, loved the kitchen, hesitant on the price. Want to nudge without pressure." /></div>
      <button className="hq-btn-primary w-full" disabled={loading} data-testid="run-email-btn">
        {loading ? 'Drafting…' : <><Sparkles size={12} className="inline mr-2"/> Draft Email</>}
      </button>
      <OutputCard loading={loading} output={output} testid="email-output" />
    </form>
  );
}

function ListingPanel({ onDone }) {
  const [f, setF] = useState({ address: '', bedrooms: 4, bathrooms: 3, sqft: 3200, features: '', style: 'luxury' });
  const [output, setOutput] = useState(''); const [loading, setLoading] = useState(false);
  const run = async e => {
    e.preventDefault(); setLoading(true); setOutput('');
    try {
      const { data } = await http.post('/ai/listing', {
        ...f, bedrooms: +f.bedrooms, bathrooms: +f.bathrooms, sqft: +f.sqft
      });
      setOutput(data.output); onDone();
    } catch (er) { toast.error(formatError(er)); }
    setLoading(false);
  };
  return (
    <form onSubmit={run} className="hq-card p-8 space-y-5">
      <div><label className="hq-label">Address</label><input className="hq-input" required value={f.address} onChange={e => setF({...f, address: e.target.value})} /></div>
      <div className="grid grid-cols-3 gap-4">
        <div><label className="hq-label">Beds</label><input className="hq-input" type="number" value={f.bedrooms} onChange={e => setF({...f, bedrooms: e.target.value})} /></div>
        <div><label className="hq-label">Baths</label><input className="hq-input" type="number" step="0.5" value={f.bathrooms} onChange={e => setF({...f, bathrooms: e.target.value})} /></div>
        <div><label className="hq-label">Sq Ft</label><input className="hq-input" type="number" value={f.sqft} onChange={e => setF({...f, sqft: e.target.value})} /></div>
      </div>
      <div><label className="hq-label">Style</label>
        <select className="hq-input" value={f.style} onChange={e => setF({...f, style: e.target.value})}>
          <option value="luxury">Luxury</option>
          <option value="modern">Modern</option>
          <option value="family">Family</option>
          <option value="investment">Investment</option>
        </select>
      </div>
      <div><label className="hq-label">Features</label><textarea className="hq-input" rows={4} required value={f.features} onChange={e => setF({...f, features: e.target.value})} /></div>
      <button className="hq-btn-primary w-full" disabled={loading} data-testid="run-listing-btn">
        {loading ? 'Crafting…' : <><Sparkles size={12} className="inline mr-2"/> Craft Listing</>}
      </button>
      <OutputCard loading={loading} output={output} testid="listing-output-mini" />
    </form>
  );
}

function ContractPanel({ onDone }) {
  const [text, setText] = useState(''); const [output, setOutput] = useState(''); const [loading, setLoading] = useState(false);
  const run = async e => {
    e.preventDefault(); if (text.trim().length < 50) return toast.error('Paste more contract text.');
    setLoading(true); setOutput('');
    try { const { data } = await http.post('/ai/contract', { contract_text: text }); setOutput(data.output); onDone(); }
    catch (er) { toast.error(formatError(er)); }
    setLoading(false);
  };
  return (
    <form onSubmit={run} className="hq-card p-8 space-y-5">
      <div><label className="hq-label">Contract Text</label><textarea className="hq-input font-mono" rows={12} value={text} onChange={e => setText(e.target.value)} placeholder="Paste the full contract text…" /></div>
      <button className="hq-btn-primary w-full" disabled={loading}>
        {loading ? 'Reading…' : <><Sparkles size={12} className="inline mr-2"/> Summarize</>}
      </button>
      <OutputCard loading={loading} output={output} testid="contract-output-mini" />
    </form>
  );
}

function MarketPanel({ onDone }) {
  const [f, setF] = useState({ neighborhood: '', city: '', focus: '' });
  const [output, setOutput] = useState(''); const [loading, setLoading] = useState(false);
  const run = async e => {
    e.preventDefault(); setLoading(true); setOutput('');
    try { const { data } = await http.post('/ai/market', f); setOutput(data.output); onDone(); }
    catch (er) { toast.error(formatError(er)); }
    setLoading(false);
  };
  return (
    <form onSubmit={run} className="hq-card p-8 space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div><label className="hq-label">Neighborhood</label><input className="hq-input" required value={f.neighborhood} onChange={e => setF({...f, neighborhood: e.target.value})} /></div>
        <div><label className="hq-label">City</label><input className="hq-input" required value={f.city} onChange={e => setF({...f, city: e.target.value})} /></div>
      </div>
      <div><label className="hq-label">Focus</label><input className="hq-input" value={f.focus} onChange={e => setF({...f, focus: e.target.value})} placeholder="Historic single-family, condos…" /></div>
      <button className="hq-btn-primary w-full" disabled={loading}>
        {loading ? 'Analyzing…' : <><Sparkles size={12} className="inline mr-2"/> Generate Intel</>}
      </button>
      <OutputCard loading={loading} output={output} testid="market-output-mini" />
    </form>
  );
}
